import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2,
  CalendarDays,
  CheckCircle2,
  XCircle,
  Clock,
  ChevronLeft,
  Briefcase,
  ArrowUpRight,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

function formatDateTimeHu(value) {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return new Intl.DateTimeFormat("hu-HU", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(d);
  } catch (e) {
    return "—";
  }
}

const OUTCOME_META = {
  successful: {
    label: "Sikeres",
    tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
    icon: CheckCircle2,
  },
  unsuccessful: {
    label: "Sikertelen",
    tone: "border-rose-200 bg-rose-50 text-rose-700",
    icon: XCircle,
  },
};

function OutcomeBadge({ value }) {
  const meta = OUTCOME_META[value];
  if (!meta) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10.5px] font-medium text-amber-800">
        <Clock className="h-3 w-3" strokeWidth={2} />
        Folyamatban
      </span>
    );
  }
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10.5px] font-medium",
        meta.tone
      )}
    >
      <Icon className="h-3 w-3" strokeWidth={2} />
      {meta.label}
    </span>
  );
}

export default function RecruitmentInterviewsPage() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [outcomeFilter, setOutcomeFilter] = useState("all");
  const [positionFilter, setPositionFilter] = useState("__all__");
  const [search, setSearch] = useState("");

  useEffect(() => {
    api
      .get("/positions/interviews/all")
      .then(({ data }) => setInterviews(data))
      .catch((e) =>
        toast.error(
          formatApiError(e?.response?.data?.detail) ||
            "Nem sikerült betölteni az interjúkat."
        )
      )
      .finally(() => setLoading(false));
  }, []);

  const positionOptions = useMemo(() => {
    const map = new Map();
    interviews.forEach((iv) => {
      if (iv.position_id && !map.has(iv.position_id)) {
        map.set(iv.position_id, iv.position_title || "—");
      }
    });
    return Array.from(map.entries()).map(([id, title]) => ({ id, title }));
  }, [interviews]);

  const stats = useMemo(() => {
    const s = { total: interviews.length, pending: 0, successful: 0, unsuccessful: 0 };
    const byPosition = new Map();
    interviews.forEach((iv) => {
      if (iv.outcome === "successful") s.successful += 1;
      else if (iv.outcome === "unsuccessful") s.unsuccessful += 1;
      else s.pending += 1;
      const key = iv.position_id || "—";
      if (!byPosition.has(key))
        byPosition.set(key, {
          title: iv.position_title || "—",
          location: iv.position_location,
          id: iv.position_id,
          total: 0,
          pending: 0,
          successful: 0,
          unsuccessful: 0,
        });
      const ent = byPosition.get(key);
      ent.total += 1;
      if (iv.outcome === "successful") ent.successful += 1;
      else if (iv.outcome === "unsuccessful") ent.unsuccessful += 1;
      else ent.pending += 1;
    });
    s.byPosition = Array.from(byPosition.values()).sort(
      (a, b) => b.total - a.total
    );
    return s;
  }, [interviews]);

  const visible = useMemo(() => {
    return interviews.filter((iv) => {
      if (outcomeFilter === "pending" && iv.outcome) return false;
      if (outcomeFilter === "successful" && iv.outcome !== "successful")
        return false;
      if (outcomeFilter === "unsuccessful" && iv.outcome !== "unsuccessful")
        return false;
      if (positionFilter !== "__all__" && iv.position_id !== positionFilter)
        return false;
      if (search.trim()) {
        const q = search.trim().toLowerCase();
        const full = `${iv.candidate_first_name || ""} ${iv.candidate_last_name || ""} ${iv.candidate_email || ""} ${iv.position_title || ""}`.toLowerCase();
        if (!full.includes(q)) return false;
      }
      return true;
    });
  }, [interviews, outcomeFilter, positionFilter, search]);

  return (
    <div data-testid="recruitment-interviews-page" className="space-y-7">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <div className="flex items-center gap-1.5 text-muted-foreground/80">
            <Link
              to="/recruitment"
              className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.22em] hover:text-foreground"
              data-testid="back-to-recruitment"
            >
              <ChevronLeft className="h-3 w-3" /> Toborzás
            </Link>
          </div>
          <h1 className="mt-1.5 text-3xl font-semibold tracking-tight">
            Interjúk összesítő
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Minden pozícióhoz rögzített interjú egy helyen — szűrhetsz pozícióra
            és eredményre, csoportosítva is láthatod a pozíciós lebontást.
          </p>
        </div>
      </header>

      {/* KPI */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <KpiCard label="Összes interjú" value={stats.total} testId="kpi-total" />
        <KpiCard
          label="Folyamatban"
          value={stats.pending}
          testId="kpi-pending"
          tone="amber"
        />
        <KpiCard
          label="Sikeres"
          value={stats.successful}
          testId="kpi-successful"
          tone="emerald"
        />
        <KpiCard
          label="Sikertelen"
          value={stats.unsuccessful}
          testId="kpi-unsuccessful"
          tone="rose"
        />
      </div>

      {/* Per-position breakdown */}
      {stats.byPosition.length > 0 && (
        <section
          data-testid="interviews-per-position"
          className="overflow-hidden rounded-2xl border border-border bg-[hsl(var(--sipx-elevated))]"
        >
          <header className="border-b border-border px-5 py-3">
            <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
              Toborzásonkénti bontás · {stats.byPosition.length} pozíció
            </p>
          </header>
          <ul className="divide-y divide-border">
            {stats.byPosition.map((p) => (
              <li key={p.id || p.title} className="px-5 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <Briefcase
                        className="h-3.5 w-3.5 text-muted-foreground"
                        strokeWidth={1.75}
                      />
                      <span className="truncate text-[13.5px] font-medium">
                        {p.title}
                      </span>
                      {p.location && (
                        <span className="truncate text-[11px] text-muted-foreground">
                          · {p.location}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span className="rounded-full bg-muted px-2 py-0.5 text-[10.5px] text-muted-foreground">
                        {p.total} interjú
                      </span>
                      {p.pending > 0 && (
                        <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10.5px] font-medium text-amber-800">
                          {p.pending} folyamatban
                        </span>
                      )}
                      {p.successful > 0 && (
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10.5px] font-medium text-emerald-700">
                          {p.successful} sikeres
                        </span>
                      )}
                      {p.unsuccessful > 0 && (
                        <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10.5px] font-medium text-rose-700">
                          {p.unsuccessful} sikertelen
                        </span>
                      )}
                    </div>
                  </div>
                  {p.id && (
                    <Link
                      to={`/recruitment/${p.id}`}
                      className="inline-flex items-center gap-1 text-[11.5px] font-medium text-muted-foreground hover:text-foreground"
                      data-testid={`open-position-${p.id}`}
                    >
                      Pozíció megnyitása
                      <ArrowUpRight className="h-3 w-3" />
                    </Link>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Filters */}
      <div className="rounded-2xl border border-border bg-[hsl(var(--sipx-elevated))] p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Jelölt vagy pozíció keresése…"
              className="pl-8"
              data-testid="interview-search"
            />
          </div>
          <Select value={outcomeFilter} onValueChange={setOutcomeFilter}>
            <SelectTrigger data-testid="interview-outcome-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Minden eredmény</SelectItem>
              <SelectItem value="pending">Folyamatban</SelectItem>
              <SelectItem value="successful">Sikeres</SelectItem>
              <SelectItem value="unsuccessful">Sikertelen</SelectItem>
            </SelectContent>
          </Select>
          <Select value={positionFilter} onValueChange={setPositionFilter}>
            <SelectTrigger data-testid="interview-position-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Minden pozíció</SelectItem>
              {positionOptions.map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Interview list */}
      <section className="overflow-hidden rounded-2xl border border-border bg-[hsl(var(--sipx-elevated))]">
        {loading ? (
          <div className="flex items-center justify-center px-6 py-14 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Interjúk betöltése…
          </div>
        ) : visible.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-6 py-14 text-center">
            <div className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background">
              <CalendarDays
                className="h-4 w-4 text-muted-foreground"
                strokeWidth={1.75}
              />
            </div>
            <p className="text-sm font-medium">Nincs interjú a szűrőkkel</p>
            <p className="text-xs text-muted-foreground">
              Próbálj eltávolítani egy szűrőt, vagy bővítsd a keresést.
            </p>
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {visible.map((iv) => (
              <li
                key={`${iv.application_id}-${iv._id}`}
                data-testid={`interview-row-${iv._id}`}
              >
                <Link
                  to={
                    iv.position_id
                      ? `/recruitment/${iv.position_id}?application=${iv.application_id}`
                      : "#"
                  }
                  className="flex items-start gap-3 px-5 py-3 transition-colors hover:bg-muted/40"
                >
                  <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-foreground/5 text-foreground">
                    <CalendarDays className="h-3.5 w-3.5" strokeWidth={1.75} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-baseline gap-x-2">
                      <span className="text-[13px] font-medium">
                        {iv.candidate_first_name} {iv.candidate_last_name}
                      </span>
                      <span className="text-[11px] text-muted-foreground">
                        · {iv.position_title || "—"}
                      </span>
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-[11.5px] text-muted-foreground">
                      <span className="font-mono">
                        {formatDateTimeHu(iv.scheduled_at)}
                      </span>
                      {iv.location && (
                        <>
                          <span>·</span>
                          <span className="truncate">{iv.location}</span>
                        </>
                      )}
                      {iv.candidate_email && (
                        <>
                          <span>·</span>
                          <span className="font-mono">{iv.candidate_email}</span>
                        </>
                      )}
                    </div>
                    {iv.notes && (
                      <p className="mt-1 line-clamp-2 text-[11.5px] text-muted-foreground/90">
                        {iv.notes}
                      </p>
                    )}
                  </div>
                  <OutcomeBadge value={iv.outcome} />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function KpiCard({ label, value, tone, testId }) {
  const tones = {
    emerald: "border-emerald-200 bg-emerald-50/60",
    amber: "border-amber-200 bg-amber-50/60",
    rose: "border-rose-200 bg-rose-50/60",
  };
  return (
    <div
      data-testid={testId}
      className={cn(
        "rounded-2xl border p-3.5 shadow-sm",
        tones[tone] || "border-border bg-[hsl(var(--sipx-elevated))]"
      )}
    >
      <p className="text-[10px] font-medium uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tracking-tight">{value}</p>
    </div>
  );
}
